import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getAllFolders } from '../../api/foldersApi';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { STATUS, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { FOLDER_SHARED_WITH_ME, FOLDER_TYPE } from '../../enums/TYPES';
import { VIDEOFEED_WITH_SIZE } from '../../components/SharedWithMe/SharedVideos/sharedVideosList';
import { SortOptions, SortType } from '../../enums/SORT';
import { getMyFoldersSortOptionAndType } from '../../helpers';

type TPaginatedResult = {
  documents: FOLDER_TYPE[];
  total: number;
};

type TQueryOptions = {
  page: number;
  limit: number;
  sort: SortType;
};

type TInitialState = {
  folders: FOLDER_TYPE[];
  total: number;
  page: number;
  limit: number;
  sort: SortType;
  sortOption: SortOptions;
  status: STATUS;
  showShareModal: boolean;
  showShareWithMeModal: boolean;
  shareFolder: FOLDER_TYPE | null;
  shareFolderForSharedWithMe: FOLDER_SHARED_WITH_ME | null;
  feedToShare: VIDEOFEED_WITH_SIZE | null;
  clickedFolderName: string;
  triggerFolderFetch: number;
};

const initialState: TInitialState = {
  folders: [],
  total: 0,
  page: 0,
  limit: 10,
  // @ts-ignore
  sort: getMyFoldersSortOptionAndType()?.split('/')[0] ? getMyFoldersSortOptionAndType()?.split('/')[0] : SortType.ASC,
  // @ts-ignore
  sortOption: getMyFoldersSortOptionAndType()?.split('/')[1] ? getMyFoldersSortOptionAndType()?.split('/')[1] : SortOptions.NAME,
  status: STATUS.IDLE,
  showShareModal: false,
  showShareWithMeModal: false,
  shareFolder: null,
  shareFolderForSharedWithMe: null,
  feedToShare: null,
  clickedFolderName: '',
  triggerFolderFetch: 0,
};

export const getFoldersThunk = createAsyncThunk(
  API_ROUTES.FOLDER,
  async ({
    page,
    limit,
    search,
    sort = SortType.ASC,
    type = VIDEOFEED_TYPE.TIMELINE,
    sortOption,
    selectedType,
    collabIds,
  }: {
    page: number;
    limit: number;
    search?: string;
    sort?: SortType;
    type?: VIDEOFEED_TYPE;
    sortOption: SortOptions;
    selectedType?: 'all' | 'personal' | 'collaborative';
    collabIds?: string[];
  }) => {
    const response = await getAllFolders({ page, limit, search, type, sort, sortOption, selectedType, collaboratorIds: collabIds });
    return response.data;
  },
);

export const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    updateTriggerFolderFetch: (state) => {
      state.triggerFolderFetch = Date.now();
    },
    removeFoldersFromList: (state, action: PayloadAction<string[]>) => {
      const folderIds = new Set(action.payload.map(String));
      const previousLength = state.folders.length;
      state.folders = state.folders.filter((folder) => !folderIds.has(String(folder.id)));
      state.total = Math.max(0, state.total - (previousLength - state.folders.length));
    },
    saveQueryOptions: (state, action: PayloadAction<TQueryOptions>) => {
      if (state.shareFolder) {
        state.shareFolder = null;
      }
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.sort = action.payload.sort;
    },
    switchShareModal: (state, action: PayloadAction<boolean>) => {
      state.showShareModal = action.payload;
      state.showShareWithMeModal = false;
    },
    switchSharedWithMeModal: (state, action: PayloadAction<boolean>) => {
      state.showShareWithMeModal = action.payload;
      state.showShareModal = false;
    },
    selectShareFolder: (state, action: PayloadAction<FOLDER_TYPE>) => {
      state.shareFolder = action.payload;
    },
    selectedSharedWithMeFolder: (state, action: PayloadAction<FOLDER_SHARED_WITH_ME>) => {
      state.shareFolderForSharedWithMe = action.payload;
      state.feedToShare = null;
    },
    removeSelectedFolder: (state) => {
      state.shareFolder = null;
      state.shareFolderForSharedWithMe = null;
    },
    setClickedFolderName: (state, action: PayloadAction<string>) => {
      state.clickedFolderName = action.payload;
    },
    updateSharedFeed: (state, action: PayloadAction<VIDEOFEED_WITH_SIZE | null>) => {
      state.feedToShare = action.payload;
      state.shareFolderForSharedWithMe = null;
    },
    updateSortOption: (state, action) => {
      state.sortOption = action.payload;
    },
    updateSortType: (state, action) => {
      state.sort = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFoldersThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getFoldersThunk.fulfilled, (state, action: PayloadAction<TPaginatedResult>) => {
        state.status = STATUS.IDLE;
        state.folders = action.payload.documents;
        state.total = action.payload.total;
      })
      .addCase(getFoldersThunk.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.status = STATUS.FAILED;
      });
  },
});

export const {
  saveQueryOptions,
  switchShareModal,
  selectShareFolder,
  removeSelectedFolder,
  selectedSharedWithMeFolder,
  switchSharedWithMeModal,
  setClickedFolderName,
  updateSharedFeed,
  updateSortOption,
  updateSortType,
  updateTriggerFolderFetch,
  removeFoldersFromList,
} = folderSlice.actions;

export default folderSlice.reducer;
